import React from "react";
import { Link, Navigate } from "react-router-dom";
import {
  CalendarDaysIcon,
  TableCellsIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import { Card, CardBody, Typography } from "@material-tailwind/react";
import { usePortalAuth } from "../context/PortalAuthContext";
import { PortalShell } from "../components/PortalShell";

function psychologistDisplayName(user) {
  const full = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();
  return full || user?.firstName || "psicólogo";
}

export default function PortalPsychologistHome() {
  const { user, isAuthenticated } = usePortalAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (user?.role !== "psychologist") {
    if (user?.role === "admin") {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/panel" replace />;
  }

  return (
    <PortalShell>
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row gap-4 sm:gap-8 items-center sm:items-center text-center sm:text-left">
        <Link to="/psicologo/perfil" className="shrink-0 group" title="Editar mi perfil">
          {user.profileImageUrl ? (
            <img
              src={user.profileImageUrl}
              alt=""
              className="h-28 w-28 sm:h-36 sm:w-36 rounded-full object-cover border-2 border-white shadow-md ring-2 ring-brand-brown/15 bg-white group-hover:ring-brand-lavender/50 transition-all"
            />
          ) : (
            <div
              className="h-28 w-28 sm:h-36 sm:w-36 rounded-full border-2 border-brand-brown/15 bg-slate-100 flex items-center justify-center text-brand-gray shadow-sm group-hover:border-brand-lavender/40 transition-all"
              aria-hidden
            >
              <UserCircleIcon className="h-16 w-16 sm:h-20 sm:w-20" />
            </div>
          )}
        </Link>
        <div className="min-w-0 flex-1">
          <Typography className="font-sans text-2xl sm:text-3xl text-brand-dark font-semibold tracking-tight">
            Hola, {psychologistDisplayName(user)}
          </Typography>
          <p className="mt-2 text-sm sm:text-base text-brand-gray leading-relaxed">
            Aquí podrás gestionar tu perfil, horario y las citas que tienes con pacientes del programa.
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-4xl">
        <Link to="/psicologo/perfil" className="group block h-full">
          <Card className="h-full border border-brand-brown/15 shadow-md hover:shadow-lg hover:border-brand-lavender/40 transition-all bg-white">
            <CardBody className="p-6 sm:p-8 flex flex-col items-start gap-4">
              <span className="inline-flex p-3 rounded-xl bg-brand-lavender/10 text-brand-lavender group-hover:bg-brand-lavender/15 transition-colors">
                <UserCircleIcon className="h-8 w-8" aria-hidden />
              </span>
              <div>
                <Typography className="font-sans text-xl text-brand-dark font-semibold">
                  Mi perfil y horario
                </Typography>
                <p className="text-sm text-brand-gray mt-1 leading-relaxed">
                  Ubicación, contacto, versiones de horario y días no disponibles.
                </p>
              </div>
            </CardBody>
          </Card>
        </Link>
        <Link to="/psicologo/citas" className="group block h-full">
          <Card className="h-full border border-brand-brown/15 shadow-md hover:shadow-lg hover:border-brand-lavender/40 transition-all bg-white">
            <CardBody className="p-6 sm:p-8 flex flex-col items-start gap-4">
              <span className="inline-flex p-3 rounded-xl bg-brand-lavender/10 text-brand-lavender group-hover:bg-brand-lavender/15 transition-colors">
                <TableCellsIcon className="h-8 w-8" aria-hidden />
              </span>
              <div>
                <Typography className="font-sans text-xl text-brand-dark font-semibold">
                  Lista de citas
                </Typography>
                <p className="text-sm text-brand-gray mt-1 leading-relaxed">
                  Consulta sesiones programadas y cancela si es necesario.
                </p>
              </div>
            </CardBody>
          </Card>
        </Link>
        <Link to="/psicologo/calendario" className="group block h-full">
          <Card className="h-full border border-brand-brown/15 shadow-md hover:shadow-lg hover:border-brand-lavender/40 transition-all bg-white">
            <CardBody className="p-6 sm:p-8 flex flex-col items-start gap-4">
              <span className="inline-flex p-3 rounded-xl bg-brand-lavender/10 text-brand-lavender group-hover:bg-brand-lavender/15 transition-colors">
                <CalendarDaysIcon className="h-8 w-8" aria-hidden />
              </span>
              <div>
                <Typography className="font-sans text-xl text-brand-dark font-semibold">
                  Calendario
                </Typography>
                <p className="text-sm text-brand-gray mt-1 leading-relaxed">
                  Vista mensual de tus sesiones agendadas.
                </p>
              </div>
            </CardBody>
          </Card>
        </Link>
      </div>
    </PortalShell>
  );
}
